import DeleteIcon from '@mui/icons-material/Delete';
import DoneIcon from '@mui/icons-material/Done';
import EditIcon from '@mui/icons-material/Edit';
import GppMaybeOutlined from '@mui/icons-material/GppMaybeOutlined';
import { Box, Chip, Typography, useTheme } from '@mui/material';
import {
  type GridSortModel,
  type GridColumns,
  type GridValidRowModel,
} from '@mui/x-data-grid';
import CustomDataGrid from 'components/customDataGrid/CustomDataGrid';
import { DialogBox } from 'components/shared';
import { strings } from 'i18n/Strings';
import { Fragment, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getAllUsers, removeEmployee } from 'service/UserService';
import { setSnackBarFailed, setSnackBarSuccess } from 'store/snackBar';
import { colorTheme } from '../../theme';
import AlertModal from './AlertModal';
import CreateUserForm from './CreateUser';
import UpdateUserForm from './UpdateUser';
import {
  setCurrentPage,
  setSortBy,
  setSortOrder,
} from 'store/component/pagination';
import { checkSortingValues } from 'utils/GeneralUtil';

interface IEmployeeProps {
  empId?: number;
  employeeId?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  password?: string | null;
  profileImage?: string | null;
  rememberToken?: string | null;
  emailVerifiedAt?: Date;
  employeeResetToken?: string | null;
  isActive?: boolean;
  createdBy?: number;
  updatedBy?: number | null;
  createdAt?: string;
  updatedAt?: string;
}

type GridRowData = Record<string, unknown>;

const Users = (): JSX.Element => {
  const currentTheme = useTheme();
  const dispatch = useDispatch();
  const colors = colorTheme(currentTheme.palette.mode);
  const [openCreateDialog, setOpenCreateDialog] = useState<boolean>(false);
  const [openUpdateDialog, setOpenUpdateDialog] = useState<boolean>(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [employeeData, setEmployeeData] = useState<GridRowData[]>([]);
  const [selectedData, setSelectedData] = useState<
    GridValidRowModel | undefined
  >();
  const ref = useRef<HTMLInputElement>(null);
  const [pageCount, setPageCount] = useState(null);
  const pageInfo = useSelector((state) => state?.pagination?.users);
  const [searchValue, setSearchValue] = useState('');
  const updateEmployeeData = (): void => {
    getAllUsers({
      page: Number(pageInfo?.page) + 1,
      size: pageInfo?.pageSize,
      sortBy: pageInfo?.sortBy,
      sortOrder: pageInfo?.sortOrder,
      searchValue,
    })
      .then((res) => {
        if (typeof res !== 'undefined' && 'basicInfo' in res) {
          const { basicInfo } = res as {
            basicInfo: GridRowData[];
          };
          if (basicInfo) {
            setEmployeeData(basicInfo?.rows);
            setPageCount(basicInfo?.count);
          }
        }
      })
      .catch((err: Error) => {
        throw err;
      });
  };

  useEffect(() => {
    updateEmployeeData();
  }, [
    pageInfo?.page,
    pageInfo?.pageSize,
    pageInfo?.sortBy,
    pageInfo?.sortOrder,
    searchValue,
  ]);
  const handleSearch = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    setSearchValue(event.target.value);
    dispatch(setCurrentPage({ key: 'users', value: 0 }));
  };
  const handleSortChange = (model: GridSortModel): void => {
    dispatch(
      setSortBy({
        key: 'users',
        value: model[0].field,
      }),
    );
    dispatch(
      setSortOrder({
        key: 'users',
        value: model[0].sort,
      }),
    );
    dispatch(setCurrentPage({ key: 'users', value: 0 }));
  };
  const columns: GridColumns<GridValidRowModel> = [
    {
      field: 'firstName',
      flex: 0.8,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.firstName}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.firstName}
        </Typography>
      ),
    },
    {
      field: 'lastName',
      flex: 0.8,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.lastName}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.lastName}
        </Typography>
      ),
    },
    {
      field: 'email',
      flex: 1.4,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.emailAddress}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.email}
        </Typography>
      ),
    },
    {
      field: 'emailVerifiedAt',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.isemailVerified}
        </Typography>
      ),
      headerAlign: 'center',
      flex: 1,
      renderCell: ({ row }) => (
        <Box m="0 auto" p="5px" display="flex" justifyContent="space-evenly">
          {row.emailVerifiedAt ? (
            <Chip
              label="Verified"
              variant="outlined"
              color="success"
              icon={<DoneIcon />}
            />
          ) : (
            <Chip
              label="Not Verified"
              variant="outlined"
              color="warning"
              icon={<GppMaybeOutlined />}
            />
          )}
        </Box>
      ),
    },
  ];

  const handleCreateDialogClose = (): void => {
    setOpenCreateDialog(false);
  };

  const handleUpdateDialogClose = (): void => {
    setOpenUpdateDialog(false);
  };

  const handleDeleteDialogClose = (): void => {
    setOpenDeleteDialog(false);
  };

  const handleDelete = async (): Promise<void> => {
    if (selectedData?.empId !== undefined) {
      const res = await removeEmployee(Number(selectedData.empId)); // convert to number
      if (res) {
        dispatch(
          setSnackBarSuccess({
            snackBarMessage: strings.displayText.userDeleteSuccess,
          }),
        );
      } else {
        dispatch(
          setSnackBarFailed({
            snackBarMessage: strings.displayText.userDeleteFailed,
          }),
        );
      }

      res && updateEmployeeData();
      setOpenDeleteDialog(false);
    }
  };

  return (
    <Fragment>
      <CustomDataGrid
        title="Users"
        columns={columns}
        rows={employeeData}
        searchEnabled={true}
        handleSearch={handleSearch}
        searchPlaceholder={strings.displayText.searchLabel9}
        addButtonTitle={'Add User'}
        onAddButtonClick={() => {
          setOpenCreateDialog(true);
          ref.current?.focus();
        }}
        actionBtnText1="edit"
        actionBtnText2="delete"
        ActionBtnIcon1={() => <EditIcon />}
        ActionBtnIcon2={() => <DeleteIcon />}
        onActionBtnClick1={(row) => {
          setSelectedData(row);
          setOpenUpdateDialog(true);
        }}
        onActionBtnClick2={(row) => {
          setSelectedData(row);
          setOpenDeleteDialog(true);
        }}
        getRowId={(row: IEmployeeProps) =>
          `${String(row.empId)}${row.email ? row.email : ''}`
        }
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
        }
        currentPage="users"
        pageCount={pageCount}
        sortModel={checkSortingValues(pageInfo)}
        sortingMode={strings.displayText.sortingServer}
        onSortModelChange={(mode) => {
          handleSortChange(mode);
        }}
      />
      <DialogBox
        open={openCreateDialog}
        onClose={handleCreateDialogClose}
        screenWidth={'md'}
        title={'Create User'}
      >
        <CreateUserForm
          onClose={handleCreateDialogClose}
          updateEmployeeData={updateEmployeeData}
        />
      </DialogBox>

      <DialogBox
        open={openUpdateDialog}
        onClose={handleUpdateDialogClose}
        screenWidth="md"
        title={strings.displayText.editUser}
      >
        <UpdateUserForm
          data={selectedData as GridValidRowModel}
          onClose={handleUpdateDialogClose}
          updateEmployeeData={updateEmployeeData}
        />
      </DialogBox>

      <AlertModal
        open={openDeleteDialog}
        onClose={handleDeleteDialogClose}
        handleDelete={handleDelete}
        title={`${strings.displayText.deleteConfirmationText} user`}
        email={selectedData?.email}
      />
    </Fragment>
  );
};

export default Users;
